import React, { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useOutletContext } from 'react-router-dom';
import {
  ResponsiveContainer,
  ComposedChart,
  BarChart,
  LineChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
} from 'recharts';
import { buildEngagementReport } from './exportReport';
import { loadEngagement, refreshEngagement } from '../../services/analyticsService';
import {
  INK, SURFACE, numFormat, pct, shortDate, addDays, axisProps,
  ChartTooltip, StatTile, Section, TableToggle, RangePicker, ExportButton,
} from './analyticsUi';
import '../../styles/analytics.css';

// Engagement tab: how many people play, how often, which games, and whether they come back.
// Data: admin-cache/engagement.json from the refreshEngagement Cloud Function.

// Games in fixed categorical order (dataviz reference palette, light mode): a game keeps its color everywhere
const GAME_META = {
  crossword: { label: 'Crossword', color: '#2a78d6' },
  wordle: { label: 'Wordle', color: '#eb6834' },
  teralehti: { label: 'Terälehti', color: '#1baf7a' },
  sudoku: { label: 'Sudoku', color: '#eda100' },
  quordle: { label: 'Quordle', color: '#e87ba4' },
  nono: { label: 'Nono', color: '#5b5f6b' },
};
const PRIMARY = '#2a78d6';
// Sequential blue (light -> dark) for heat cells
const RAMP = ['#cde2fb', '#9ec5f4', '#6da7ec', '#3987e5', '#256abf', '#184f95', '#0d366b'];
const RANGES = [
  { value: 90, label: '90 days' },
  { value: 180, label: '6 months' },
  { value: 365, label: '1 year' },
  { value: 0, label: 'All' },
];
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
// BigQuery DAYOFWEEK: 1 = Sunday
const BQ_DOW_ORDER = [2, 3, 4, 5, 6, 7, 1];
const MIN_COHORT_USERS = 10;

const TABS = [
  { to: '/admin/engagement', label: 'Overview', end: true },
  { to: '/admin/engagement/games', label: 'Games' },
  { to: '/admin/engagement/puzzles', label: 'Puzzles' },
  { to: '/admin/engagement/growth', label: 'New users & paywall' },
  { to: '/admin/engagement/devices', label: 'Timing & devices' },
];

const num = (v) => (v == null || Number.isNaN(v) ? '–' : numFormat.format(Math.round(v)));
const ratio = (a, b) => (b ? a / b : null);
const avg = (rows, field) => (rows.length ? rows.reduce((s, r) => s + (r[field] || 0), 0) / rows.length : null);
const sum = (rows, field) => rows.reduce((s, r) => s + (r[field] || 0), 0);
const minutes = (sec) => (sec == null ? '–' : `${Math.floor(sec / 60)}:${String(Math.round(sec % 60)).padStart(2, '0')}`);
const weekLabel = (key) => `${shortDate(key)} ${key.slice(2, 4)}`;
const weekday = (key) => WEEKDAYS[(new Date(`${key}T00:00:00Z`).getUTCDay() + 6) % 7];
const puzzleId = (key) => `${key.slice(8, 10)}-${key.slice(5, 7)}-${key.slice(0, 4)}`;
const gameLabel = (g) => GAME_META[g]?.label || g;

// Ramp color for a value in [0, max]; dark steps get white text
function heat(value, max) {
  if (value == null || !max) return { background: 'transparent', color: INK.muted };
  const i = Math.min(RAMP.length - 1, Math.floor((value / max) * (RAMP.length - 1) + 0.0001));
  return { background: RAMP[i], color: i >= 3 ? '#ffffff' : '#0b0b0b' };
}

// Adds a trailing 7-day average of `fields` (null until 7 days are available)
function withAverages(rows, fields) {
  return rows.map((r, i) => {
    const out = { ...r };
    const window = rows.slice(Math.max(0, i - 6), i + 1);
    fields.forEach(f => { out[`${f}Avg`] = window.length === 7 ? avg(window, f) : null; });
    return out;
  });
}

const sliceRange = (rows, days) => {
  if (!days || !rows.length) return rows;
  const from = addDays(rows[rows.length - 1].date, -(days - 1));
  return rows.filter(r => r.date >= from);
};

const tooltipCursor = { fill: 'rgba(11,11,11,0.04)' };
const legendProps = { iconType: 'circle', wrapperStyle: { fontSize: 13, color: INK.secondary } };
const lineDot = (color) => ({ r: 4, strokeWidth: 2, stroke: SURFACE, fill: color });

function Empty({ children }) {
  return <p className="pf-empty">{children}</p>;
}

// Horizontal bars for a short list of { label, value } with the share written beside each bar
function ShareBars({ rows, color = PRIMARY, total, valueLabel = 'Users' }) {
  if (!rows.length) return <Empty>No data yet.</Empty>;
  const base = total ?? rows.reduce((s, r) => s + r.value, 0);
  const data = rows.map(r => ({ ...r, share: ratio(r.value, base) }));
  return (
    <ResponsiveContainer width="100%" height={Math.max(80, data.length * 34 + 16)}>
      <BarChart data={data} layout="vertical" margin={{ top: 0, right: 48, bottom: 0, left: 0 }}>
        <XAxis type="number" hide />
        <YAxis type="category" dataKey="label" width={130} {...axisProps} axisLine={false} interval={0} />
        <Tooltip content={<ChartTooltip valueFormatter={(v, p) => `${num(v)} (${pct(p.payload.share)})`} />} cursor={tooltipCursor} />
        <Bar isAnimationActive={false} dataKey="value" name={valueLabel} fill={color} maxBarSize={20} radius={[0, 4, 4, 0]}>
          <LabelList dataKey="share" position="right" formatter={pct} style={{ fill: INK.secondary, fontSize: 12 }} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// --- Layout: header, data loading, sub-tabs ---

const helsinkiDate = (d) => d.toLocaleDateString('en-CA', { timeZone: 'Europe/Helsinki' });

function EngagementLayout() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  // No scheduled refresh (it's costly): rebuild on the first open of the day, otherwise via the button
  useEffect(() => {
    loadEngagement().then(cached => {
      setData(cached);
      setLoading(false);
      const builtToday = cached?.lastUpdated && helsinkiDate(new Date(cached.lastUpdated)) === helsinkiDate(new Date());
      if (!builtToday) handleRefresh();
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleRefresh() {
    setRefreshing(true);
    setError(null);
    try {
      await refreshEngagement();
      setData(await loadEngagement());
    } catch (e) {
      console.error('Refresh failed:', e);
      setError(e.message || 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  }

  return (
    <div className="pf-panel">
      <div className="pf-header">
        <div>
          <h1>Engagement</h1>
        </div>
        <div className="pf-header-actions">
          {data?.lastUpdated && (
            <span className="pf-updated">
              Updated {new Date(data.lastUpdated).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <ExportButton data={data} name="engagement" buildReport={() => buildEngagementReport(data)} />
          <button className="btn-secondary" onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? 'Refreshing… (a minute or two)' : 'Refresh now'}
          </button>
        </div>
      </div>

      {(error || data?.warnings?.length > 0) && (
        <div className="pf-warnings">
          <strong>Some data could not be loaded</strong>
          <ul>
            {error && <li>{error}</li>}
            {(data?.warnings || []).map(w => <li key={w}>{w}</li>)}
          </ul>
        </div>
      )}

      <nav className="subtabs">
        {TABS.map(tab => (
          <NavLink key={tab.to} to={tab.to} end={tab.end} className={({ isActive }) => `subtab-button ${isActive ? 'active' : ''}`}>
            {tab.label}
          </NavLink>
        ))}
      </nav>

      {loading ? (
        <p className="pf-empty">Loading…</p>
      ) : !data ? (
        <Section title="No data yet" hint={refreshing ? "Building the first summary. It reads every user's daily games, so it takes a minute or two." : "Press Refresh now to build the first summary."} />
      ) : (
        <Outlet context={{ data }} />
      )}
    </div>
  );
}

// --- Overview: active users, players per game, habit, streaks, retention ---

export function EngagementOverview() {
  const { data } = useOutletContext();
  const [rangeDays, setRangeDays] = useState(180);
  const [showRetentionTable, setShowRetentionTable] = useState(false);
  const players = data.players;
  const activity = data.activity || [];

  const view = useMemo(() => {
    const act = withAverages(activity.map(d => ({ ...d, stickiness: d.mau ? d.dau / d.mau : null })), ['dau', 'stickiness']);
    const lastAct = act.slice(-7);
    const prevAct = act.slice(-14, -7);
    const dailyPlayers = withAverages(players?.dailyPlayers || [], ['players', ...data.games]);
    // Firestore days run to today, which is still in progress
    const fullDays = dailyPlayers.slice(0, -1);
    return {
      act,
      dauNow: avg(lastAct, 'dau'),
      dauPrev: prevAct.length === 7 ? avg(prevAct, 'dau') : null,
      mauNow: act.length ? act[act.length - 1].mau : null,
      mauPrev: act.length > 28 ? act[act.length - 29].mau : null,
      installsNow: sum(lastAct, 'installs'),
      installsPrev: prevAct.length === 7 ? sum(prevAct, 'installs') : null,
      playersNow: avg(fullDays.slice(-7), 'players'),
      playersPrev: fullDays.length >= 14 ? avg(fullDays.slice(-14, -7), 'players') : null,
      dailyPlayers: fullDays,
    };
  }, [activity, players, data.games]);

  const stickinessNow = view.dauNow != null && view.mauNow ? view.dauNow / view.mauNow : null;
  const actRange = sliceRange(view.act, rangeDays);
  const playersRange = sliceRange(view.dailyPlayers, rangeDays);
  const habit = players?.habit;
  const streaks = players?.streaks;
  const regular = habit ? habit.daysPlayed.filter(b => parseInt(b.label, 10) >= 8).reduce((s, b) => s + b.users, 0) : 0;

  return (
    <>
      <Section title="Last 7 days" hint="Active = opened the app (Google Analytics). Players = signed-in users who finished a daily game.">
        <div className="pf-tiles pf-tiles-top">
          <StatTile hero label="Daily active users (average)" value={view.dauNow} previous={view.dauPrev} format={num} period="7 days" />
          <StatTile label="Monthly active users" value={view.mauNow} previous={view.mauPrev} format={num} period="28 days" />
          <StatTile label="Stickiness (daily ÷ monthly)" value={stickinessNow} format={pct} />
          <StatTile label="Daily players" value={view.playersNow} previous={view.playersPrev} format={num} period="7 days" />
          <StatTile label="New installs" value={view.installsNow} previous={view.installsPrev} format={num} period="7 days" />
        </div>
      </Section>

      <Section
        title="Active users"
        actions={<RangePicker options={RANGES} value={rangeDays} onChange={setRangeDays} />}
      >
        {actRange.length ? (
          <div className="pf-chart">
            <ResponsiveContainer width="100%" height={240}>
              <ComposedChart data={actRange} syncId="engagement-daily" margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid vertical={false} stroke={INK.grid} />
                <XAxis dataKey="date" tickFormatter={shortDate} minTickGap={24} {...axisProps} />
                <YAxis allowDecimals={false} width={56} {...axisProps} axisLine={false} />
                <Tooltip content={<ChartTooltip labelFormatter={shortDate} valueFormatter={num} />} cursor={tooltipCursor} />
                <Legend {...legendProps} />
                <Bar isAnimationActive={false} dataKey="dau" name="Active users" fill="#b7d3f6" maxBarSize={12} radius={[4, 4, 0, 0]} />
                <Line isAnimationActive={false} dataKey="dauAvg" name="7-day average" stroke={PRIMARY} strokeWidth={2} dot={false} connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        ) : <Empty>No Google Analytics history loaded.</Empty>}
      </Section>

      <Section
        title="Players per game"
        hint="Signed-in players who finished each daily game, 7-day average."
      >
        {playersRange.length ? (
          <div className="pf-chart">
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={playersRange} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid vertical={false} stroke={INK.grid} />
                <XAxis dataKey="date" tickFormatter={shortDate} minTickGap={24} {...axisProps} />
                <YAxis allowDecimals={false} width={56} {...axisProps} axisLine={false} />
                <Tooltip content={<ChartTooltip labelFormatter={shortDate} valueFormatter={v => (v == null ? '–' : num(v))} />} />
                <Legend {...legendProps} />
                <Line isAnimationActive={false} dataKey="playersAvg" name="Any game" stroke={INK.secondary} strokeWidth={2} strokeDasharray="4 3" dot={false} connectNulls />
                {data.games.map(g => (
                  <Line key={g} isAnimationActive={false} dataKey={`${g}Avg`} name={gameLabel(g)} stroke={GAME_META[g].color} strokeWidth={2} dot={false} connectNulls />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : <Empty>No daily game history in Firestore yet.</Empty>}
      </Section>

      {habit && (
        <Section
          title="How often players come"
          hint={`${num(habit.players)} players finished a daily game in the 4 weeks ${shortDate(habit.from)} – ${shortDate(habit.to)}. ${pct(ratio(regular, habit.players))} played on 8 or more days.`}
        >
          <div className="pf-grid-2">
            <div>
              <div className="pf-chart-title">Days played out of 28</div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={habit.daysPlayed} margin={{ top: 16, right: 8, bottom: 0, left: 0 }}>
                  <CartesianGrid vertical={false} stroke={INK.grid} />
                  <XAxis dataKey="label" {...axisProps} />
                  <YAxis allowDecimals={false} width={48} {...axisProps} axisLine={false} />
                  <Tooltip content={<ChartTooltip labelFormatter={l => `${l} days`} valueFormatter={v => `${num(v)} (${pct(ratio(v, habit.players))})`} />} cursor={tooltipCursor} />
                  <Bar isAnimationActive={false} dataKey="users" name="Players" fill={PRIMARY} maxBarSize={36} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div>
              <div className="pf-chart-title">Share of players who played each game</div>
              <ShareBars
                rows={data.games.map(g => ({ label: gameLabel(g), value: habit.gameReach[g] || 0 }))}
                total={habit.players}
                valueLabel="Players"
              />
            </div>
          </div>
        </Section>
      )}

      {streaks && (
        <Section title="Streaks" hint="Streaks still alive: the player finished a game today or yesterday.">
          <div className="pf-tiles">
            <StatTile label="Alive streaks" value={streaks.alive} format={num} />
            <StatTile label="7 days or longer" value={streaks.atLeast7} format={num} note={pct(ratio(streaks.atLeast7, streaks.alive))} />
            <StatTile label="30 days or longer" value={streaks.atLeast30} format={num} note={pct(ratio(streaks.atLeast30, streaks.alive))} />
            <StatTile label="Longest" value={streaks.longest} format={v => `${num(v)} days`} />
          </div>
        </Section>
      )}

      {players?.retention && (
        <RetentionSection
          retention={players.retention}
          open={showRetentionTable}
          onToggle={() => setShowRetentionTable(o => !o)}
        />
      )}
    </>
  );
}

function RetentionSection({ retention, open, onToggle }) {
  const { windows } = retention;
  const cohorts = retention.cohorts.filter(c => c.users >= MIN_COHORT_USERS);
  // Weighted average over the latest 12 cohorts that have reached each window
  const typical = windows.map(w => {
    const reached = cohorts.filter(c => c.rates[w.key] != null).slice(-12);
    const users = reached.reduce((s, c) => s + c.users, 0);
    return {
      key: w.key,
      label: w.label,
      value: users ? reached.reduce((s, c) => s + c.rates[w.key] * c.users, 0) / users : null,
      cohorts: reached.length,
    };
  });
  const columnMax = Object.fromEntries(windows.map(w => [w.key, Math.max(0, ...cohorts.map(c => c.rates[w.key] ?? 0))]));

  return (
    <Section
      title="Do new users come back?"
      hint="Share of new signed-in users who finished a daily game in each period after signing up (average of the latest 12 signup weeks)."
      actions={<TableToggle open={open} onToggle={onToggle} />}
    >
      {typical.some(t => t.value != null) ? (
        <div className="pf-chart">
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={typical} margin={{ top: 20, right: 24, bottom: 0, left: 0 }}>
              <CartesianGrid vertical={false} stroke={INK.grid} />
              <XAxis dataKey="label" {...axisProps} />
              <YAxis tickFormatter={pct} width={48} {...axisProps} axisLine={false} domain={[0, 'auto']} />
              <Tooltip content={<ChartTooltip valueFormatter={pct} />} />
              <Line isAnimationActive={false} dataKey="value" name="New users who played" stroke={PRIMARY} strokeWidth={2} dot={lineDot(PRIMARY)}>
                <LabelList dataKey="value" position="top" formatter={pct} style={{ fill: INK.secondary, fontSize: 12 }} />
              </Line>
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : <Empty>No signup cohorts old enough yet.</Empty>}
      {open && (
        <div className="pf-table-wrap">
          <table className="pf-table pf-heat-table">
            <thead>
              <tr><th>Signup week</th><th>New users</th>{windows.map(w => <th key={w.key}>{w.label}</th>)}</tr>
            </thead>
            <tbody>
              {[...cohorts].reverse().map(c => (
                <tr key={c.week}>
                  <td>{weekLabel(c.week)}</td>
                  <td>{num(c.users)}</td>
                  {windows.map(w => {
                    const v = c.rates[w.key];
                    return <td key={w.key} style={heat(v, columnMax[w.key])}>{v == null ? '' : pct(v)}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="pf-footnote">Day 0 is the signup day; Week 4 is days 22–28. Columns are shaded against their best week; empty = not old enough yet. Weeks under {MIN_COHORT_USERS} new users are hidden.</p>
        </div>
      )}
    </Section>
  );
}

// --- Games: funnels and per-game details (BigQuery) ---

function BigQueryMissing() {
  return <Section title="No event data" hint="The Firebase Analytics → BigQuery export could not be read. See the warnings above." />;
}

// The export only has days since it was turned on, which can be later than the requested start
const eventsFrom = (events) => [events.daily[0]?.date, events.since].filter(Boolean).sort().pop();
const eventsRange = (events) => `${shortDate(eventsFrom(events))} – ${shortDate(events.until)}`;
const sinceNote = (events) => `${eventsRange(events)}, counted per device.`;

export function EngagementGames() {
  const { data } = useOutletContext();
  const ev = data.events;
  if (!ev) return <BigQueryMissing />;

  const daily = ev.crossword.find(r => r.isDaily);
  const archive = ev.crossword.find(r => !r.isDaily);
  const wordle = ev.games.find(g => g.game === 'wordle');
  const teralehti = ev.games.find(g => g.game === 'teralehti');
  const guessTotal = ev.wordleGuesses.reduce((s, r) => s + r.events, 0);
  const guesses = ev.wordleGuesses.map(r => ({ ...r, share: ratio(r.events, guessTotal) }));
  const rankBase = teralehti?.started || 0;
  const ranks = ev.teralehtiRanks.map(r => ({ label: `Rank ${Number(r.value) + 1}`, value: r.events }));
  const quordleWin = ev.quordleWins.find(r => r.value === 'true')?.events || 0;
  const quordleAll = ev.quordleWins.reduce((s, r) => s + r.events, 0);
  const sudokuRows = ev.sudokuDifficulty.started.map(s => ({
    difficulty: s.value,
    started: s.events,
    finished: ev.sudokuDifficulty.finished.find(f => f.value === s.value)?.events || 0,
  }));

  return (
    <>
      <Section title="Games" hint={`How many start each game, and how many get to the end. ${sinceNote(ev)}`}>
        <div className="pf-table-wrap">
          <table className="pf-table">
            <thead>
              <tr><th>Game</th><th>Players</th><th>Games started</th><th>Players who finished</th><th>Finish rate</th></tr>
            </thead>
            <tbody>
              {ev.games.map(g => (
                <tr key={g.game}>
                  <td><span className="pf-swatch pf-inline-swatch" style={{ background: GAME_META[g.game].color }} />{gameLabel(g.game)}</td>
                  <td>{num(g.startedUsers)}</td>
                  <td>{num(g.started)}</td>
                  <td>{g.finishedUsers == null ? '–' : num(g.finishedUsers)}</td>
                  <td>{g.finishedUsers == null ? '–' : pct(ratio(g.finishedUsers, g.startedUsers))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="pf-footnote">
          Wordle counts only wins as finished. Terälehti has no end; see its ranks below.
        </p>
      </Section>

      <Section title="Crossword" hint="First solves of crosswords finished in the app.">
        <div className="pf-tiles">
          <StatTile label="Median solve time, daily" value={daily?.quartiles?.[2]} format={minutes} note={daily ? `Middle half ${minutes(daily.quartiles[1])}–${minutes(daily.quartiles[3])}` : null} />
          <StatTile label="Solved without hints" value={daily?.noHintShare} format={pct} />
          <StatTile label="Hints per solve" value={daily?.avgHints} format={v => (v == null ? '–' : v.toFixed(1))} />
          <StatTile label="Archive solves" value={archive?.completions || 0} format={num} note={`vs ${num(daily?.completions || 0)} daily solves`} />
        </div>
      </Section>

      <div className="pf-grid-2">
        <Section title="Wordle guesses" hint={`Wins by number of guesses. ${pct(ratio(wordle?.finishedUsers, wordle?.startedUsers))} of players won at least once.`}>
          {guesses.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={guesses} margin={{ top: 20, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid vertical={false} stroke={INK.grid} />
                <XAxis dataKey="value" {...axisProps} />
                <YAxis allowDecimals={false} width={48} {...axisProps} axisLine={false} />
                <Tooltip content={<ChartTooltip labelFormatter={l => `${l} guesses`} valueFormatter={(v, p) => `${num(v)} (${pct(p.payload.share)})`} />} cursor={tooltipCursor} />
                <Bar isAnimationActive={false} dataKey="events" name="Wins" fill={GAME_META.wordle.color} maxBarSize={40} radius={[4, 4, 0, 0]}>
                  <LabelList dataKey="share" position="top" formatter={pct} style={{ fill: INK.secondary, fontSize: 12 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <Empty>No wins recorded yet.</Empty>}
        </Section>

        <Section title="Terälehti ranks" hint={`Games reaching each rank, out of ${num(rankBase)} started. Free players hit the word cap ${num(ev.teralehtiCapHits?.events)} times.`}>
          <ShareBars rows={ranks} total={rankBase || undefined} color={GAME_META.teralehti.color} valueLabel="Games" />
        </Section>
      </div>

      <div className="pf-grid-2">
        <Section title="Quordle" hint="Finished games that were won.">
          <div className="pf-tiles">
            <StatTile label="Win rate" value={ratio(quordleWin, quordleAll)} format={pct} note={`${num(quordleWin)} of ${num(quordleAll)} games`} />
          </div>
        </Section>
        <Section title="Sudoku by difficulty" hint="Games started and finished.">
          {sudokuRows.length ? (
            <table className="pf-table">
              <thead><tr><th>Difficulty</th><th>Started</th><th>Finished</th><th>Finish rate</th></tr></thead>
              <tbody>
                {sudokuRows.map(r => (
                  <tr key={r.difficulty}><td>{r.difficulty}</td><td>{num(r.started)}</td><td>{num(r.finished)}</td><td>{pct(ratio(r.finished, r.started))}</td></tr>
                ))}
              </tbody>
            </table>
          ) : <Empty>No sudoku games yet.</Empty>}
        </Section>
      </div>
    </>
  );
}

// --- Puzzles: each day's crossword and Terälehti (Firestore community stats) ---

export function EngagementPuzzles() {
  const { data } = useOutletContext();
  const [showAll, setShowAll] = useState(false);
  const puzzles = data.puzzles;
  if (!puzzles) return <Section title="No puzzle stats" hint="Puzzle stats could not be read. See the warnings above." />;

  const today = data.today;
  const crosswords = puzzles.crosswords.filter(p => p.starters > 0 && p.date <= today);
  const chart = crosswords.map(p => ({
    date: p.date,
    minutes: p.medianSolveSeconds != null ? p.medianSolveSeconds / 60 : null,
  }));
  const byWeekday = WEEKDAYS.map(day => {
    const rows = crosswords.filter(p => weekday(p.date) === day && p.medianSolveSeconds != null);
    return { label: day, minutes: rows.length ? avg(rows, 'medianSolveSeconds') / 60 : null };
  });
  const tableRows = [...crosswords].reverse().slice(0, showAll ? undefined : 14);
  const teralehti = [...puzzles.teralehti.filter(p => p.players > 0 && p.date <= today)].reverse();

  return (
    <>
      <Section
        title="Crosswords"
        hint="Median solve time per day's crossword (signed-in solvers). Long bars are the hard ones."
      >
        {chart.length ? (
          <div className="pf-chart">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={chart} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid vertical={false} stroke={INK.grid} />
                <XAxis dataKey="date" tickFormatter={shortDate} minTickGap={24} {...axisProps} />
                <YAxis tickFormatter={v => `${Math.round(v)} min`} width={56} {...axisProps} axisLine={false} />
                <Tooltip
                  content={<ChartTooltip labelFormatter={k => `${weekday(k)} ${shortDate(k)}`} valueFormatter={v => minutes(v * 60)} />}
                  cursor={tooltipCursor}
                />
                <Bar isAnimationActive={false} dataKey="minutes" name="Median solve time" fill={GAME_META.crossword.color} maxBarSize={16} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <div className="pf-chart-title">Average by weekday</div>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={byWeekday} margin={{ top: 20, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid vertical={false} stroke={INK.grid} />
                <XAxis dataKey="label" {...axisProps} />
                <YAxis tickFormatter={v => `${Math.round(v)} min`} width={56} {...axisProps} axisLine={false} />
                <Tooltip content={<ChartTooltip valueFormatter={v => minutes(v * 60)} />} cursor={tooltipCursor} />
                <Bar isAnimationActive={false} dataKey="minutes" name="Median solve time" fill={GAME_META.crossword.color} maxBarSize={40} radius={[4, 4, 0, 0]}>
                  <LabelList dataKey="minutes" position="top" formatter={v => minutes(v * 60)} style={{ fill: INK.secondary, fontSize: 12 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : <Empty>No crossword stats yet.</Empty>}

        {tableRows.length > 0 && (
          <div className="pf-table-wrap">
            <table className="pf-table pf-left-cols">
              <thead>
                <tr><th>Puzzle</th><th>Started</th><th>Solved</th><th>Solve rate</th><th>Median time</th><th>Usually solved last</th></tr>
              </thead>
              <tbody>
                {tableRows.map(p => (
                  <tr key={p.date}>
                    <td><Link to={`/admin/crossword/${puzzleId(p.date)}`}>{weekday(p.date)} {shortDate(p.date)}</Link></td>
                    <td>{num(p.starters)}</td>
                    <td>{num(p.solvers)}</td>
                    <td>{pct(ratio(p.solvers, p.starters))}</td>
                    <td>{minutes(p.medianSolveSeconds)}</td>
                    <td className="pf-text-cell">
                      {p.lastSolved ? <><b>{p.lastSolved.answer}</b> <span className="pf-muted">{p.lastSolved.clue}</span></> : '–'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {crosswords.length > 14 && (
              <button className="pf-link-button" onClick={() => setShowAll(s => !s)}>{showAll ? 'Show fewer' : `Show all ${crosswords.length}`}</button>
            )}
          </div>
        )}
        <p className="pf-footnote">
          "Usually solved last" is often the hardest clue. Started = stayed 15 seconds in the puzzle.
        </p>
      </Section>

      <Section title="Terälehti" hint="Players per day and the share of words a typical player found.">
        {teralehti.length ? (
          <div className="pf-table-wrap">
            <table className="pf-table pf-left-cols">
              <thead><tr><th>Puzzle</th><th>Players</th><th>Words</th><th>Words found</th><th>Least found</th></tr></thead>
              <tbody>
                {teralehti.map(p => (
                  <tr key={p.date}>
                    <td>{weekday(p.date)} {shortDate(p.date)}</td>
                    <td>{num(p.players)}</td>
                    <td>{num(p.words)}</td>
                    <td>{pct(p.avgFoundShare)}</td>
                    <td className="pf-text-cell">{p.leastFound.join(', ') || '–'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <Empty>No Terälehti stats yet.</Empty>}
      </Section>
    </>
  );
}

// --- New users & paywall (BigQuery + Firestore onboarding answers) ---

export function EngagementGrowth() {
  const { data } = useOutletContext();
  const ev = data.events;
  if (!ev) return <BigQueryMissing />;

  // Coming back is only counted for install days whose users have had the time
  const act = ev.activation;
  const matureD1 = act.filter(r => r.date <= addDays(ev.until, -1));
  const matureW1 = act.filter(r => r.date <= addDays(ev.until, -7));
  const installs = sum(act, 'installs');
  const funnel = [
    { label: 'Installed', value: installs, base: installs },
    { label: 'Finished onboarding', value: sum(act, 'onboarded'), base: installs },
    { label: 'Started a game', value: sum(act, 'played'), base: installs },
    { label: 'Finished a game', value: sum(act, 'finished'), base: installs },
    { label: 'Saw the offer', value: sum(act, 'sawOffer'), base: installs },
    { label: 'Came back next day', value: sum(matureD1, 'd1'), base: sum(matureD1, 'installs') },
    { label: 'Came back within 7 days', value: sum(matureW1, 'w1'), base: sum(matureW1, 'installs') },
  ].map(f => ({ ...f, share: ratio(f.value, f.base) }));
  const ob = ev.onboarding;
  const paywall = ev.paywall.filter(p => p.viewed || p.purchase_attempt || p.purchased);
  const toRows = (list) => list.map(r => ({ label: r.value, value: r.users }));

  return (
    <>
      <Section title="New users' first steps" hint={`What devices installed ${eventsRange(ev)} did afterwards. Coming back only counts install days that are old enough.`}>
        {installs ? (
          <div className="pf-chart">
            <ResponsiveContainer width="100%" height={funnel.length * 38 + 16}>
              <BarChart data={funnel} layout="vertical" margin={{ top: 0, right: 56, bottom: 0, left: 0 }}>
                <XAxis type="number" hide domain={[0, 1]} />
                <YAxis type="category" dataKey="label" width={170} {...axisProps} axisLine={false} interval={0} />
                <Tooltip content={<ChartTooltip valueFormatter={(v, p) => `${pct(v)} (${num(p.payload.value)} of ${num(p.payload.base)})`} />} cursor={tooltipCursor} />
                <Bar isAnimationActive={false} dataKey="share" name="Share of installs" fill={PRIMARY} maxBarSize={22} radius={[0, 4, 4, 0]}>
                  <LabelList dataKey="share" position="right" formatter={pct} style={{ fill: INK.secondary, fontSize: 12 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : <Empty>No installs in the export yet.</Empty>}
        <p className="pf-footnote">
          Engaged users (3 crosswords in the first week, the Google Ads goal): {num(ev.engagedConversions)}.
          Allowed notifications: {pct(ratio(ob.notifications.granted, ob.notifications.asked))} in onboarding,
          {' '}{pct(ratio(ob.streakNotifications.granted, ob.streakNotifications.asked))} at the streak prompt.
        </p>
      </Section>

      <Section title="Onboarding answers" hint="What new users said about themselves while onboarding.">
        <div className="pf-grid-3">
          <div>
            <div className="pf-chart-title">How they found Norppa</div>
            <ShareBars rows={toRows(ob.discovery)} />
          </div>
          <div>
            <div className="pf-chart-title">Why they play</div>
            <ShareBars rows={toRows(ob.motivation)} />
          </div>
          <div>
            <div className="pf-chart-title">Crossword experience</div>
            <ShareBars rows={toRows(ob.experience)} />
          </div>
        </div>
      </Section>

      <Section title="Subscription offer" hint="Where devices saw the offer and how many went on to buy. Purchases include trials started.">
        {paywall.length ? (
          <div className="pf-table-wrap">
            <table className="pf-table">
              <thead><tr><th>Opened from</th><th>Saw the offer</th><th>Tried to buy</th><th>Bought</th><th>Bought / saw</th></tr></thead>
              <tbody>
                {paywall.map(p => (
                  <tr key={p.source}>
                    <td>{p.source}</td><td>{num(p.viewed)}</td><td>{num(p.purchase_attempt)}</td><td>{num(p.purchased)}</td>
                    <td>{pct(ratio(p.purchased, p.viewed))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <Empty>No offer views yet.</Empty>}
      </Section>
    </>
  );
}

// --- Timing & devices (BigQuery) ---

export function EngagementDevices() {
  const { data } = useOutletContext();
  const ev = data.events;
  if (!ev) return <BigQueryMissing />;

  const cell = {};
  ev.heatmap.forEach(h => { cell[`${h.dow}-${h.hour}`] = h.events; });
  const max = Math.max(0, ...ev.heatmap.map(h => h.events));
  const hourTotals = Array.from({ length: 24 }, (_, hour) => ({ hour, events: ev.heatmap.filter(h => h.hour === hour).reduce((s, h) => s + h.events, 0) }));
  const peak = [...hourTotals].sort((a, b) => b.events - a.events)[0];

  const platforms = {};
  ev.devices.forEach(d => { platforms[d.platform] = (platforms[d.platform] || 0) + d.users; });
  const platformTotal = Object.values(platforms).reduce((s, v) => s + v, 0);
  const versions = [...ev.devices].sort((a, b) => b.users - a.users);
  const n = ev.notifications;

  return (
    <>
      <Section
        title="When people play"
        hint={`Games started by weekday and hour (Helsinki time). ${peak?.events ? `Busiest hour: ${peak.hour}:00–${peak.hour + 1}:00.` : ''} Useful for timing notifications.`}
      >
        {max ? (
          <div className="pf-table-wrap">
            <table className="pf-heatmap" aria-label="Games started by weekday and hour">
              <thead>
                <tr><th />{Array.from({ length: 24 }, (_, h) => <th key={h}>{h % 3 === 0 ? h : ''}</th>)}</tr>
              </thead>
              <tbody>
                {BQ_DOW_ORDER.map((dow, i) => (
                  <tr key={dow}>
                    <th>{WEEKDAYS[i]}</th>
                    {Array.from({ length: 24 }, (_, h) => {
                      const v = cell[`${dow}-${h}`] || 0;
                      return <td key={h} style={{ background: v ? heat(v, max).background : 'var(--pf-plane)' }} title={`${WEEKDAYS[i]} ${h}:00–${h + 1}:00: ${num(v)} games`} />;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="pf-heat-legend">
              <span>Fewer</span>
              {RAMP.map(c => <span key={c} className="pf-heat-swatch" style={{ background: c }} />)}
              <span>More</span>
            </div>
          </div>
        ) : <Empty>No games in the export yet.</Empty>}
      </Section>

      <div className="pf-grid-2">
        <Section title="Platforms" hint="Active devices in the last 7 days.">
          <ShareBars rows={Object.entries(platforms).map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value)} total={platformTotal} valueLabel="Devices" />
        </Section>
        <Section title="Notifications" hint={sinceNote(ev)}>
          <div className="pf-tiles">
            <StatTile label="Opened" value={ratio(n.opened, n.received)} format={pct} note={`${num(n.opened)} of ${num(n.received)} received`} />
            <StatTile label="Swiped away" value={ratio(n.dismissed, n.received)} format={pct} note={num(n.dismissed)} />
          </div>
          <p className="pf-footnote">Received is only reported on Android, so the rates are Android's.</p>
        </Section>
      </div>

      <Section title="App versions" hint="Active devices in the last 7 days. Old versions miss newer events.">
        <div className="pf-table-wrap">
          <table className="pf-table">
            <thead><tr><th>Version</th><th>Platform</th><th>Devices</th><th>Share</th></tr></thead>
            <tbody>
              {versions.slice(0, 8).map(v => (
                <tr key={`${v.platform}-${v.version}`}><td>{v.version}</td><td>{v.platform}</td><td>{num(v.users)}</td><td>{pct(ratio(v.users, platformTotal))}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="pf-footnote">App errors logged: {num(ev.exceptions?.events)} on {num(ev.exceptions?.users)} devices ({eventsRange(ev)}).</p>
      </Section>
    </>
  );
}

export default EngagementLayout;
